import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { IonIcon, IonModal } from "@ionic/react";
import { closeOutline, chevronForwardOutline, sendSharp } from "ionicons/icons";
import { Group } from "@visx/group";
import { LinePath } from "@visx/shape";
import { scaleLinear } from "@visx/scale";
import { GridRows, GridColumns } from "@visx/grid";
import { AxisLeft, AxisBottom } from "@visx/axis";
import { curveLinear } from "@visx/curve";
import { useBLE } from "../../../hooks/useBle";
import {
  CurveAnalysisCardProps,
  DataPoint,
  GraphType,
  TherapySession,
} from "../../../contexts/types";
import { CurvesLoading } from "./ProgressIndicator";
import { Capacitor } from "@capacitor/core";
import { ScreenOrientation } from "@capacitor/screen-orientation";

// Configuration
const MIN_BRUSH_SELECTION = 100; // 100ms minimum for dragging
const INITIAL_CLICK_SELECTION = 500; // 500ms for initial click selection

// Custom Checkbox Component
const CustomCheckbox: React.FC<{
  label: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}> = ({ label, checked, onChange, disabled = false }) => {
  const labelId = label.toLowerCase();
  const gradientId = `gradient_${labelId}`;
  const clipPathId = `clip_${labelId}`;

  return (
    <button
      onClick={disabled ? undefined : onChange}
      disabled={disabled}
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        padding: "0px",
        gap: "6px",
        height: "20px",
        background: "transparent",
        border: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        flex: "none",
        opacity: disabled ? 0.5 : 1,
        transition: "opacity 0.2s ease",
      }}
    >
      <div
        style={{
          width: "20px",
          height: "20px",
          position: "relative",
          flex: "none",
        }}
      >
        {checked ? (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <g clipPath={`url(#${clipPathId})`}>
              <path
                d="M15.8333 2.5H4.16667C3.24167 2.5 2.5 3.25 2.5 4.16667V15.8333C2.5 16.75 3.24167 17.5 4.16667 17.5H15.8333C16.7583 17.5 17.5 16.75 17.5 15.8333V4.16667C17.5 3.25 16.7583 2.5 15.8333 2.5ZM8.33333 14.1667L4.16667 10L5.34167 8.825L8.33333 11.8083L14.6583 5.48333L15.8333 6.66667L8.33333 14.1667Z"
                fill={`url(#${gradientId})`}
              />
            </g>
            <defs>
              <linearGradient
                id={gradientId}
                x1="2.5"
                y1="10.8851"
                x2="17.5"
                y2="10.8851"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#01B7C4" />
                <stop offset="1" stopColor="#00ACD7" />
              </linearGradient>
              <clipPath id={clipPathId}>
                <rect width="20" height="20" fill="white" />
              </clipPath>
            </defs>
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M4.16667 17.5C3.70833 17.5 3.31583 17.3369 2.98917 17.0108C2.66306 16.6842 2.5 16.2917 2.5 15.8333V4.16667C2.5 3.70833 2.66306 3.31583 2.98917 2.98917C3.31583 2.66306 3.70833 2.5 4.16667 2.5H15.8333C16.2917 2.5 16.6842 2.66306 17.0108 2.98917C17.3369 3.31583 17.5 3.70833 17.5 4.16667V15.8333C17.5 16.2917 17.3369 16.6842 17.0108 17.0108C16.6842 17.3369 16.2917 17.5 15.8333 17.5H4.16667ZM4.16667 15.8333H15.8333V4.16667H4.16667V15.8333Z"
              fill="#B0B4B7"
            />
          </svg>
        )}
      </div>
      <span
        style={{
          fontFamily: "Figtree, sans-serif",
          fontStyle: "normal",
          fontWeight: "600",
          fontSize: "13px",
          lineHeight: "20px",
          display: "flex",
          alignItems: "center",
          background: checked
            ? "linear-gradient(90deg, #01B7C4 0%, #00ACD7 100%)"
            : "unset",
          WebkitBackgroundClip: checked ? "text" : "unset",
          WebkitTextFillColor: checked ? "transparent" : "#B0B4B7",
          backgroundClip: checked ? "text" : "unset",
          color: checked ? "transparent" : "#B0B4B7",
          flex: "none",
        }}
      >
        {label}
      </span>
    </button>
  );
};

// Enhanced Brush Component for historical data with responsive width support
const HistoricalBrushComponent: React.FC<{
  data: DataPoint[];
  brushRange: [number, number] | null;
  onBrushChange: (range: [number, number] | null) => void;
  allTimeRange: [number, number];
  dataType?: string;
  dataColor?: string;
  containerWidth?: number;
}> = ({
  data,
  brushRange,
  onBrushChange,
  allTimeRange,
  dataType = "pressure",
  dataColor = "#22D3EE",
  containerWidth,
}) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState<"start" | "end" | "area" | null>(
    null
  );
  const [dragStartPos, setDragStartPos] = useState<{
    x: number;
    initialRange: [number, number];
  } | null>(null);
  const [sliderWidth, setSliderWidth] = useState(containerWidth || 346);

  // Update slider width when container width changes
  useEffect(() => {
    const updateWidth = () => {
      if (sliderRef.current) {
        setSliderWidth(sliderRef.current.offsetWidth || containerWidth || 346);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [containerWidth]);

  const [minTime, maxTime] = allTimeRange;
  const timeSpan = maxTime - minTime;
  const displayRange: [number, number] = brushRange || [minTime, maxTime];

  const handleMouseDown =
    (type: "start" | "end" | "area") => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(type);
      if (type === "area" && brushRange) {
        setDragStartPos({
          x: e.clientX,
          initialRange: [...brushRange],
        });
      }
    };

  const handleTouchStart =
    (type: "start" | "end" | "area") => (e: React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(type);
      if (type === "area" && brushRange) {
        const touch = e.touches[0] || e.changedTouches[0];
        setDragStartPos({
          x: touch.clientX,
          initialRange: [...brushRange],
        });
      }
    };

  const handleDoubleClick = () => {
    onBrushChange(null);
  };

  const getEventX = (e: MouseEvent | TouchEvent): number => {
    if ("touches" in e) {
      return e.touches[0]?.clientX || e.changedTouches[0]?.clientX || 0;
    }
    return e.clientX;
  };

  const handleMove = (e: MouseEvent | TouchEvent) => {
    if (!isDragging || !sliderRef.current) return;
    e.preventDefault();
    const rect = sliderRef.current.getBoundingClientRect();
    const x = getEventX(e) - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newTime = minTime + percentage * timeSpan;

    if (isDragging === "area" && brushRange && dragStartPos) {
      const deltaX = getEventX(e) - dragStartPos.x;
      const deltaTime = (deltaX / rect.width) * timeSpan;
      const rangeWidth =
        dragStartPos.initialRange[1] - dragStartPos.initialRange[0];
      let newStart = dragStartPos.initialRange[0] + deltaTime;
      let newEnd = dragStartPos.initialRange[1] + deltaTime;
      if (newStart < minTime) {
        newStart = minTime;
        newEnd = newStart + rangeWidth;
      }
      if (newEnd > maxTime) {
        newEnd = maxTime;
        newStart = newEnd - rangeWidth;
      }
      onBrushChange([newStart, newEnd]);
    } else if (brushRange) {
      const minSeparation = Math.max(MIN_BRUSH_SELECTION, timeSpan * 0.02);
      if (isDragging === "start") {
        const maxAllowedStart = brushRange[1] - minSeparation;
        const constrainedTime = Math.min(newTime, maxAllowedStart);
        onBrushChange([constrainedTime, brushRange[1]]);
      } else if (isDragging === "end") {
        const minAllowedEnd = brushRange[0] + minSeparation;
        const constrainedTime = Math.max(newTime, minAllowedEnd);
        onBrushChange([brushRange[0], constrainedTime]);
      }
    } else {
      const halfClickSelection = INITIAL_CLICK_SELECTION / 2;
      const newStart = Math.max(minTime, newTime - halfClickSelection);
      const newEnd = Math.min(maxTime, newTime + halfClickSelection);
      onBrushChange([newStart, newEnd]);
    }
  };

  const handleEnd = () => {
    setIsDragging(null);
    setDragStartPos(null);
  };

  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e: MouseEvent) => handleMove(e);
      const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault();
        handleMove(e);
      };
      const handleMouseUp = () => handleEnd();
      const handleTouchEnd = () => handleEnd();

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd, { passive: false });

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [isDragging, brushRange, minTime, maxTime, timeSpan]);

  const startPercentage =
    timeSpan > 0 ? ((displayRange[0] - minTime) / timeSpan) * 100 : 0;
  const endPercentage =
    timeSpan > 0 ? ((displayRange[1] - minTime) / timeSpan) * 100 : 100;

  const getScaledRange = () => {
    if (data.length === 0) return [-50, 50];
    const values = data.map((p) => p.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = Math.max((max - min) * 0.1, 1);
    return [min - padding, max + padding];
  };

  const [minValue, maxValue] = getScaledRange();

  return (
    <div
      style={{
        width: "100%",
        maxWidth: "100%",
        height: "46px",
        background: "#000000",
        borderRadius: "8px",
        position: "relative",
        margin: "16px auto 0",
      }}
    >
      <div
        ref={sliderRef}
        style={{
          position: "absolute",
          width: "100%",
          height: "30px",
          left: "0px",
          top: "8px",
          background: "#000000",
          borderRadius: "4px",
          overflow: "hidden",
          cursor: brushRange ? "default" : "crosshair",
        }}
        onDoubleClick={handleDoubleClick}
        onMouseDown={
          brushRange
            ? undefined
            : (e) => {
                const rect = sliderRef.current?.getBoundingClientRect();
                if (rect) {
                  const x = e.clientX - rect.left;
                  const percentage = x / rect.width;
                  const clickTime = minTime + percentage * timeSpan;
                  const halfClickSelection = INITIAL_CLICK_SELECTION / 2;
                  const newStart = Math.max(
                    minTime,
                    clickTime - halfClickSelection
                  );
                  const newEnd = Math.min(
                    maxTime,
                    clickTime + halfClickSelection
                  );
                  onBrushChange([newStart, newEnd]);
                }
              }
        }
        onTouchStart={
          brushRange
            ? undefined
            : (e) => {
                const rect = sliderRef.current?.getBoundingClientRect();
                if (rect) {
                  const touch = e.touches[0] || e.changedTouches[0];
                  const x = touch.clientX - rect.left;
                  const percentage = x / rect.width;
                  const clickTime = minTime + percentage * timeSpan;
                  const halfClickSelection = INITIAL_CLICK_SELECTION / 2;
                  const newStart = Math.max(
                    minTime,
                    clickTime - halfClickSelection
                  );
                  const newEnd = Math.min(
                    maxTime,
                    clickTime + halfClickSelection
                  );
                  onBrushChange([newStart, newEnd]);
                }
              }
        }
      >
        <svg
          style={{
            pointerEvents: "none",
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
          viewBox={`0 0 ${sliderWidth} 30`}
        >
          {data.length > 1 && (
            <polyline
              fill="none"
              stroke={dataColor}
              strokeWidth="1.5"
              opacity="0.8"
              points={data
                .filter(
                  (point) => point.time >= minTime && point.time <= maxTime
                )
                .map((point) => {
                  const x =
                    timeSpan > 0
                      ? Math.min(
                          sliderWidth,
                          Math.max(
                            0,
                            ((point.time - minTime) / timeSpan) * sliderWidth
                          )
                        )
                      : 0;
                  const normalizedValue =
                    maxValue > minValue
                      ? (point.value - minValue) / (maxValue - minValue)
                      : 0.5;
                  const y = Math.min(
                    30,
                    Math.max(0, (1 - normalizedValue) * 30)
                  );
                  return `${x},${y}`;
                })
                .join(" ")}
            />
          )}
        </svg>

        {brushRange && (
          <div
            style={{
              position: "absolute",
              top: "0",
              bottom: "0",
              left: `${startPercentage}%`,
              width: `${endPercentage - startPercentage}%`,
              background: "rgba(255, 255, 255, 0.15)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              cursor: "move",
              pointerEvents: "auto",
            }}
            onMouseDown={handleMouseDown("area")}
            onTouchStart={handleTouchStart("area")}
          />
        )}
      </div>

      {brushRange && (
        <>
          <div
            style={{
              position: "absolute",
              width: "44px",
              height: "46px",
              left: `${startPercentage}%`,
              top: "0px",
              cursor: "ew-resize",
              zIndex: 10,
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              touchAction: "none",
              background:
                isDragging === "start"
                  ? "rgba(255,255,255,0.1)"
                  : "transparent",
              borderRadius: "8px",
              transition: "background 0.2s ease",
            }}
            onMouseDown={handleMouseDown("start")}
            onTouchStart={handleTouchStart("start")}
          >
            <div
              style={{
                width: "4px",
                height: "12px",
                background: "#FFFFFF",
                borderRadius: "2px",
                boxShadow:
                  isDragging === "start"
                    ? "0 0 8px rgba(255, 255, 255, 0.8)"
                    : "0 0 2px rgba(0, 0, 0, 0.3)",
                transition: "box-shadow 0.1s ease",
              }}
            />
          </div>

          <div
            style={{
              position: "absolute",
              width: "44px",
              height: "46px",
              left: `${endPercentage}%`,
              top: "0px",
              cursor: "ew-resize",
              zIndex: 10,
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              touchAction: "none",
              background:
                isDragging === "end" ? "rgba(255,255,255,0.1)" : "transparent",
              borderRadius: "8px",
              transition: "background 0.2s ease",
            }}
            onMouseDown={handleMouseDown("end")}
            onTouchStart={handleTouchStart("end")}
          >
            <div
              style={{
                width: "4px",
                height: "12px",
                background: "#FFFFFF",
                borderRadius: "2px",
                boxShadow:
                  isDragging === "end"
                    ? "0 0 8px rgba(255, 255, 255, 0.8)"
                    : "0 0 2px rgba(0, 0, 0, 0.3)",
                transition: "box-shadow 0.1s ease",
              }}
            />
          </div>
        </>
      )}

      <div
        style={{
          position: "absolute",
          top: "-20px",
          left: "8px",
          right: "60px",
          fontSize: "10px",
          color: dataColor,
          fontFamily: "Figtree, sans-serif",
          fontWeight: "500",
          textTransform: "uppercase",
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        }}
      >
        {dataType.toUpperCase()}
      </div>
    </div>
  );
};

// Enhanced Graph Component with visx and expand icon
const HistoricalGraphComponent: React.FC<{
  data: DataPoint[];
  graphType: GraphType;
  width: number;
  height: number;
  brushRange: [number, number] | null;
  isMobile: boolean;
  graphCount: number;
  onExpand: (graphType: string) => void;
  showExpandIcon: boolean;
}> = ({
  data,
  graphType,
  width,
  height,
  brushRange,
  isMobile,
  graphCount,
  onExpand,
  showExpandIcon,
}) => {
  const margin = useMemo(() => {
    const baseBottomMargin = isMobile ? 45 : 40;
    const additionalBottomMargin = isMobile && graphCount > 2 ? 10 : 0;

    return {
      top: 20,
      right: isMobile ? 50 : 60,
      bottom: baseBottomMargin + additionalBottomMargin,
      left: isMobile ? 70 : 80,
    };
  }, [isMobile, graphCount]);

  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  const filteredData = useMemo(() => {
    if (data.length === 0) return [];

    if (brushRange) {
      return data.filter(
        (d) => d.time >= brushRange[0] && d.time <= brushRange[1]
      );
    }
    return data;
  }, [data, brushRange]);

  const yRange = useMemo(() => {
    if (filteredData.length === 0) return graphType.range;

    const values = filteredData.map((d) => d.value);
    const dataMin = Math.min(...values, 0);
    const dataMax = Math.max(...values, 0);
    const padding = Math.max((dataMax - dataMin) * 0.1, 1);

    return [dataMin - padding, dataMax + padding];
  }, [filteredData, graphType.range]);

  const xScale = useMemo(() => {
    if (filteredData.length === 0) {
      return scaleLinear({
        range: [0, xMax],
        domain: [0, 1000],
      });
    }

    const times = filteredData.map((d) => d.time);
    const minTime = Math.min(...times);
    const maxTime = Math.max(...times);

    return scaleLinear({
      range: [0, xMax],
      domain: [minTime, maxTime],
    });
  }, [xMax, filteredData]);

  const yScale = useMemo(
    () =>
      scaleLinear({
        range: [yMax, 0],
        domain: yRange,
      }),
    [yMax, yRange]
  );

  return (
    <div
      style={{
        width,
        height,
        background: "#2C2C2E",
        borderRadius: "8px",
        position: "relative",
        marginBottom: "8px",
        border: "1px solid rgba(255, 255, 255, 0.08)",
        boxShadow:
          "0 2px 8px rgba(0, 0, 0, 0.25), 0 1px 2px rgba(0, 0, 0, 0.1)",
      }}
    >
      {showExpandIcon && (
        <div
          style={{
            position: "absolute",
            top: "8px",
            right: "8px",
            zIndex: 1000,
          }}
        >
          <button
            onClick={() => onExpand(graphType.type)}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "opacity 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "0.8";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "1";
            }}
          >
            <svg
              width="30"
              height="30"
              viewBox="0 0 30 30"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="30" height="30" rx="15" fill="#363636" />
              <path
                d="M20.8346 9.1665L15.8346 9.1665L15.8346 10.8332L19.168 10.8332L19.168 14.1665L20.8346 14.1665L20.8346 9.1665ZM14.168 19.1665L10.8346 19.1665L10.8346 15.8332L9.16797 15.8332L9.16797 20.8332L14.168 20.8332L14.168 19.1665Z"
                fill="#B0B4B7"
              />
            </svg>
          </button>
        </div>
      )}

      <svg width={width} height={height}>
        <Group left={margin.left} top={margin.top}>
          <GridRows
            scale={yScale}
            width={xMax}
            strokeDasharray="2,2"
            stroke="#363636"
            strokeOpacity={0.6}
          />
          <GridColumns
            scale={xScale}
            height={yMax}
            strokeDasharray="2,2"
            stroke="#363636"
            strokeOpacity={0.3}
          />

          {yRange[0] <= 0 && yRange[1] >= 0 && (
            <line
              x1={0}
              y1={yScale(0)}
              x2={xMax}
              y2={yScale(0)}
              stroke="#FFFFFF"
              strokeWidth={0.5}
              strokeOpacity={0.8}
              strokeDasharray="4,4"
            />
          )}

          {filteredData.length > 0 && (
            <LinePath
              data={filteredData}
              x={(d) => xScale(d.time)}
              y={(d) => yScale(d.value)}
              stroke={graphType.color}
              strokeWidth={2}
              curve={curveLinear}
            />
          )}

          {filteredData.length <= 50 &&
            filteredData.map((point, index) => (
              <circle
                key={index}
                cx={xScale(point.time)}
                cy={yScale(point.value)}
                r={1.5}
                fill={graphType.color}
              />
            ))}

          <AxisLeft
            scale={yScale}
            tickFormat={(value) => Number(value).toFixed(graphType.decimals)}
            stroke="#87868B"
            tickStroke="#87868B"
            tickLabelProps={() => ({
              fontSize: isMobile ? 10 : 11,
              fontFamily: "Figtree, sans-serif",
              fill: "#87868B",
              textAnchor: "end",
              dx: isMobile ? -8 : -12,
              dy: 4,
            })}
          />

          <AxisBottom
            top={yMax}
            scale={xScale}
            numTicks={isMobile ? 3 : Math.min(6, Math.floor(xMax / 80))}
            tickFormat={(value) => {
              const elapsedSeconds = Number(value) / 1000;
              const mins = Math.floor(elapsedSeconds / 60);
              const secs = Math.floor(elapsedSeconds % 60);
              return `${mins.toString().padStart(2, "0")}:${secs
                .toString()
                .padStart(2, "0")}`;
            }}
            stroke="#87868B"
            tickStroke="#87868B"
            tickLabelProps={() => ({
              fontSize: isMobile ? 10 : 11,
              fontFamily: "Figtree, sans-serif",
              fill: "#87868B",
              textAnchor: "middle",
              dy: isMobile ? 14 : 12,
            })}
          />
        </Group>

        <text
          x={margin.left - (isMobile ? 8 : 12)}
          y={15}
          fill="#87868B"
          fontSize={isMobile ? "10px" : "11px"}
          fontWeight="400"
          fontFamily="Figtree"
          textAnchor="end"
        >
          {graphType.unit}
        </text>

        <text
          x={width - (showExpandIcon ? 60 : 24)}
          y={25}
          fill="#87868B"
          fontSize={isMobile ? "9px" : "10px"}
          fontFamily="Figtree"
          textAnchor="end"
        >
          {graphType.type.toUpperCase()}
        </text>
      </svg>
    </div>
  );
};

// Therapy Selector Component
// Enhanced Stable TherapySelector with scroll behavior matching titration card
const TherapySelector = React.memo(
  ({
    isOpen,
    onClose,
    onSelect,
    curvesList,
    modalKey,
  }: {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (therapy: TherapySession) => void;
    curvesList: TherapySession[];
    modalKey: string;
  }) => {
    // Refs to persist scroll position across ALL re-renders
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const scrollPositionRef = useRef(0);

    // console.log(
    //   `Curve TherapySelector RENDER - Key: ${modalKey}, Open: ${isOpen}, List Length: ${curvesList?.length}`
    // );

    // Background scroll lock
    useEffect(() => {
      if (isOpen) {
        const originalStyle = window.getComputedStyle(document.body).overflow;
        document.body.style.overflow = "hidden";
        console.log("Background scroll locked");

        return () => {
          document.body.style.overflow = originalStyle;
          console.log("Background scroll unlocked");
        };
      }
    }, [isOpen]);

    // Restore scroll position after each render
    useEffect(() => {
      const container = scrollContainerRef.current;
      if (container && isOpen && scrollPositionRef.current > 0) {
        requestAnimationFrame(() => {
          container.scrollTop = scrollPositionRef.current;
          console.log(`Restored scroll to: ${scrollPositionRef.current}`);
        });
      }
    });

    // Handle scroll with persistence
    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
      const newScrollTop = e.currentTarget.scrollTop;
      scrollPositionRef.current = newScrollTop;
      console.log(`Scroll position saved: ${newScrollTop}`);
    }, []);

    const handleModalClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
    }, []);

    const handleBackdropClick = useCallback(() => {
      onClose();
    }, [onClose]);

    if (!isOpen || !curvesList) return null;

    return (
      <div
        className="fixed inset-0 z-[2000]"
        onClick={handleBackdropClick}
        style={{
          background: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          padding: "0 0 50px 0",
          touchAction: "none", // Prevent background scroll on touch devices
        }}
      >
        <div
          onClick={handleModalClick}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "30px 20px",
            gap: "32px",
            width: "362px",
            maxWidth: "calc(100vw - 32px)",
            height: "385px",
            background: "#222222",
            borderRadius: "30px",
            position: "relative",
            touchAction: "auto", // Allow scroll inside modal
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "0px",
              gap: "30px",
              width: "100%",
              maxWidth: "309px",
              height: "28px",
              position: "relative",
            }}
          >
            <h2
              style={{
                width: "100%",
                height: "28px",
                fontFamily: "Figtree, sans-serif",
                fontStyle: "normal",
                fontWeight: "700",
                fontSize: "24px",
                lineHeight: "28px",
                textAlign: "center",
                color: "#FFFFFF",
                margin: 0,
              }}
            >
              Select Therapy
            </h2>
            <button
              onClick={onClose}
              style={{
                position: "absolute",
                width: "30px",
                height: "30px",
                right: "0px",
                top: "-12.66px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IonIcon
                icon={closeOutline}
                style={{
                  color: "#B0B4B7",
                  width: "24px",
                  height: "24px",
                }}
              />
            </button>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              padding: "0px",
              gap: "6px",
              width: "100%",
              maxWidth: "322px",
              height: "280px",
            }}
          >
            <h3
              style={{
                width: "100%",
                height: "14px",
                fontFamily: "Figtree, sans-serif",
                fontStyle: "normal",
                fontWeight: "500",
                fontSize: "12px",
                lineHeight: "14px",
                textTransform: "uppercase",
                color: "#FFFFFF",
                margin: 0,
                flexShrink: 0, // Prevent header from shrinking
              }}
            >
              {curvesList.length > 0
                ? `LAST ${curvesList.length} THERAPIES`
                : "NO Therapies Found on Device"}
            </h3>

            {/* FIXED: Added proper scroll container with all necessary properties */}
            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                padding: "0px",
                width: "100%",
                height: "260px",
                overflowY: "auto", // ADDED: Enable vertical scrolling
                overflowX: "hidden", // ADDED: Hide horizontal scroll
                scrollbarWidth: "thin", // ADDED: Thin scrollbar for Firefox
                scrollbarColor: "#666 #222", // ADDED: Scrollbar colors
                scrollBehavior: "auto", // ADDED: No smooth scrolling interference
                WebkitOverflowScrolling: "touch", // ADDED: iOS smooth scrolling
                paddingRight: "8px", // ADDED: Reserve scrollbar space
                marginRight: "-8px", // ADDED: Compensate for padding
                // ADDED: Hardware acceleration for better performance
                willChange: "scroll-position",
                transform: "translateZ(0)",
              }}
              // ADDED: Event handlers to prevent background scrolling
              onWheel={(e) => {
                e.stopPropagation();
                const element = e.currentTarget;
                const { scrollTop, scrollHeight, clientHeight } = element;

                if (
                  (scrollTop === 0 && e.deltaY < 0) ||
                  (scrollTop + clientHeight >= scrollHeight && e.deltaY > 0)
                ) {
                  e.preventDefault();
                }
              }}
              onTouchMove={(e) => {
                e.stopPropagation();
              }}
            >
              {curvesList.map((therapy, index) => (
                <button
                  key={`therapy-${therapy.refId}-${therapy.date}`} // Stable key
                  onClick={() => {
                    console.log(`Selected therapy: ${therapy.refId}`);
                    onSelect(therapy);
                    onClose();
                  }}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "16px 0px",
                    gap: "12px",
                    width: "100%",
                    minHeight: "52px", // CHANGED: Use minHeight instead of fixed height
                    background: "#222222",
                    borderTop: index === 0 ? "none" : "1px solid #363636",
                    borderRadius:
                      index === 0
                        ? "12px 12px 0px 0px"
                        : index === curvesList.length - 1
                        ? "0px 0px 12px 12px"
                        : "0px",
                    border: "none",
                    cursor: "pointer",
                    flexShrink: 0, // ADDED: Prevent items from shrinking
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      padding: "0px",
                      gap: "8px",
                      height: "20px",
                      margin: "0 auto",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "Figtree, sans-serif",
                        fontStyle: "normal",
                        fontWeight: "400",
                        fontSize: "14px",
                        lineHeight: "20px",
                        letterSpacing: "0.374px",
                        color: "#FFFFFF",
                      }}
                    >
                      {therapy.date}
                    </span>
                    <span
                      style={{
                        fontFamily: "Figtree, sans-serif",
                        fontStyle: "normal",
                        fontWeight: "400",
                        fontSize: "14px",
                        lineHeight: "17px",
                        letterSpacing: "0.374px",
                        color: "#B0B4B7",
                      }}
                    >
                      ({therapy.time})
                    </span>
                    {therapy.locallyAvailable && (
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: "#22C55E",
                          marginLeft: "4px",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </div>

                  <IonIcon
                    icon={chevronForwardOutline}
                    style={{
                      width: "20px",
                      height: "20px",
                      color: "#B0B4B7",
                      flexShrink: 0,
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if the modal key changes or open state changes
    const shouldSkipRender =
      prevProps.modalKey === nextProps.modalKey &&
      prevProps.isOpen === nextProps.isOpen;

    // console.log("Curve TherapySelector memo check:", {
    //   prevKey: prevProps.modalKey,
    //   nextKey: nextProps.modalKey,
    //   prevOpen: prevProps.isOpen,
    //   nextOpen: nextProps.isOpen,
    //   shouldSkipRender,
    // });

    return shouldSkipRender;
  }
);

TherapySelector.displayName = "CurveTherapySelector";

// Main Enhanced Curve Analysis Card Component
const EnhancedCurveAnalysisCard: React.FC<CurveAnalysisCardProps> = ({
  data,
  isLoading,
  onSync,
}) => {
  const {
    curvesList,
    isConnected,
    storedCurveDataDeviceId,
    setCurveData,
    openModal,
    device,
  } = useBLE();
  const [showTherapySelector, setShowTherapySelector] = useState(false);
  const [selectedTherapy, setSelectedTherapy] = useState<TherapySession | null>(
    null
  );
  const [previousSelectedTherapy, setPreviousSelectedTherapy] =
    useState<TherapySession | null>(null);

  const [modalData, setModalData] = useState({
    curvesList: [] as TherapySession[],
    isOpen: false,
    modalKey: "",
    timestamp: 0,
  });

  const stableCallbacks = useRef({
    onClose: () => setShowTherapySelector(false),
    onSelect: async (therapy: TherapySession) => {
      setBrushRange(null); // Reset brush range for new data
      await onSync(therapy, true, true, true); // Always fetch all 3
      setSelectedTherapy((prevtherapy) => {
        setPreviousSelectedTherapy(prevtherapy);
        return therapy;
      });
    },
  });

  // **NEW: Landscape mode state and transition handling**
  const [forcedLandscape, setForcedLandscape] = useState(false);
  const [selectedLandscapeGraph, setSelectedLandscapeGraph] = useState<
    string | null
  >(null);
  const [preLandscapeGraphState, setPreLandscapeGraphState] = useState<
    GraphType[]
  >([]);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const isLandscape = forcedLandscape || window.innerWidth > window.innerHeight;

  // All 3 graphs enabled by default
  const [graphTypes, setGraphTypes] = useState<GraphType[]>([
    {
      type: "flow",
      enabled: true,
      color: "#fbfb24",
      unit: "LPM",
      range: [-900, 900],
      decimals: 1,
    },
    {
      type: "pressure",
      enabled: true,
      color: "#22D3EE",
      unit: "cmH2O",
      range: [-150, 150],
      decimals: 1,
    },
    {
      type: "volume",
      enabled: true,
      color: "#f73434",
      unit: "mL",
      range: [-50000, 50000],
      decimals: 1,
    },
  ]);

  const [brushRange, setBrushRange] = useState<[number, number] | null>(null);
  const [bottomSpacing, setBottomSpacing] = useState(16);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 480);
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);

  // Calculate all-time range and set default selection (10% to 90%)
  const allTimeRange = useMemo((): [number, number] => {
    if (!data) return [0, 1000];

    const allData = [
      ...data.pressureData,
      ...data.flowData,
      ...data.volumeData,
    ];

    if (allData.length === 0) return [0, 1000];

    const times = allData.map((d) => d.time);
    return [Math.min(...times), Math.max(...times)];
  }, [data]);

  // Set default selection to 10%-90% of data when data changes
  useEffect(() => {
    if (
      data &&
      selectedTherapy &&
      selectedTherapy.refId !== previousSelectedTherapy?.refId
    ) {
      // console.log(
      //   "Setting default brush range for new therapy:",
      //   selectedTherapy,
      //   brushRange,
      //   previousSelectedTherapy,
      //   selectedTherapy,
      //   allTimeRange
      // );
      const [minTime, maxTime] = allTimeRange;
      const totalDuration = maxTime - minTime;
      const defaultStart = minTime + totalDuration * 0.1; // 10%
      const defaultEnd = minTime + totalDuration * 0.9; // 90%
      setBrushRange([defaultStart, defaultEnd]);
    }
  }, [data, allTimeRange, selectedTherapy, previousSelectedTherapy]);

  useEffect(() => {
    // console.log("Brush range updated:", brushRange);
  }, [brushRange]);

  // Update the snapshot only when modal state changes
  useEffect(() => {
    if (showTherapySelector && !modalData.isOpen) {
      // Modal just opened - take snapshot
      const timestamp = Date.now();
      // console.log(
      //   "Curve modal opening - taking snapshot of curves list",
      //   curvesList?.length
      // );
      setModalData({
        curvesList: curvesList || [],
        isOpen: true,
        modalKey: `curve-modal-${timestamp}`,
        timestamp,
      });
    } else if (!showTherapySelector && modalData.isOpen) {
      // Modal just closed - reset
      // console.log("Curve modal closing - clearing snapshot");
      setModalData({
        curvesList: [],
        isOpen: false,
        modalKey: "",
        timestamp: 0,
      });
    }
  }, [showTherapySelector, modalData.isOpen, curvesList]);

  useEffect(() => {
    stableCallbacks.current.onSelect = async (therapy: TherapySession) => {
      setBrushRange(null);
      await onSync(therapy, true, true, true);
      setSelectedTherapy((prevtherapy) => {
        setPreviousSelectedTherapy(prevtherapy);
        return therapy;
      });
    };
  }, [onSync]);

  // Enhanced landscape orientation functions with smooth transition
  const forceEnterLandscape = async (graphType: string) => {
    setIsTransitioning(true);
    setPreLandscapeGraphState([...graphTypes]);
    setSelectedLandscapeGraph(graphType);

    // Wait a frame for the transition state to render
    // await new Promise((resolve) => requestAnimationFrame(resolve));

    if (Capacitor.isNativePlatform()) {
      try {
        await ScreenOrientation.lock({ orientation: "landscape" });
        // Give the orientation change time to complete
        await new Promise((resolve) => setTimeout(resolve, 100));
      } catch (error) {
        console.warn("Failed to force landscape:", error);
      }
    }

    setForcedLandscape(true);
    setIsTransitioning(false);
  };

  const exitLandscape = async () => {
    setIsTransitioning(true);
    setSelectedLandscapeGraph(null);
    if (preLandscapeGraphState.length > 0) {
      setGraphTypes([...preLandscapeGraphState]);
    }

    // Wait a frame for the transition state to render (hide modal first)
    // await new Promise((resolve) => requestAnimationFrame(resolve));

    // Start orientation change
    if (Capacitor.isNativePlatform()) {
      try {
        await ScreenOrientation.lock({ orientation: "portrait" });
        // Give the orientation change time to complete
        await new Promise((resolve) => setTimeout(resolve, 100));
      } catch (error) {
        console.warn("Failed to return to portrait:", error);
      }
    }

    // Now clean up states
    setForcedLandscape(false);
    setIsTransitioning(false);
  };

  // **IMPROVED: Dynamic width calculations for responsive design**
  const getContainerWidth = () => {
    if (containerRef) {
      const rect = containerRef.getBoundingClientRect();
      return rect.width - 32; // Account for padding
    }

    const screenWidth = window.innerWidth;

    // iPad Pro 11" and 13" specific handling
    if (screenWidth >= 1024) {
      return Math.min(screenWidth - 64, screenWidth * 0.9); // Use 90% of screen width with reasonable margins
    } else if (screenWidth >= 768) {
      return screenWidth - 48; // Tablet margins
    } else {
      return screenWidth - 32; // Mobile margins
    }
  };

  const getAvailableWidth = () => {
    const screenWidth = window.innerWidth;

    if (isLandscape) {
      return Math.min(screenWidth - 32, 1400);
    } else {
      return getContainerWidth();
    }
  };

  // Dynamic spacing calculations
  useEffect(() => {
    const calculateBottomSpacing = () => {
      const vh = window.innerHeight;
      let calculatedSpacing = 16;

      if (isMobile) {
        if (vh < 700) calculatedSpacing = Math.max(12, vh * 0.02);
        else if (vh < 800) calculatedSpacing = Math.max(16, vh * 0.025);
        else if (vh < 900) calculatedSpacing = Math.max(18, vh * 0.028);
        else calculatedSpacing = Math.max(20, vh * 0.03);

        const aspectRatio =
          Math.max(window.innerWidth, vh) / Math.min(window.innerWidth, vh);
        if (aspectRatio > 2.0 && vh > 750) {
          calculatedSpacing += Math.min(12, vh * 0.015);
        }
      }

      setBottomSpacing(Math.round(calculatedSpacing));
      setIsMobile(window.innerWidth < 480);
    };

    calculateBottomSpacing();
    window.addEventListener("resize", calculateBottomSpacing);
    return () => window.removeEventListener("resize", calculateBottomSpacing);
  }, [isMobile, forcedLandscape]);

  // Auto-select the most recent therapy
  useEffect(() => {
    if (curvesList && curvesList.length > 0 && !selectedTherapy) {
      setSelectedTherapy(curvesList[0]);
    }
  }, [curvesList, selectedTherapy]);

  const [quickTransitionSupport, setQuickTransitionSupport] =
    useState<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Start or continue the interval when therapy is not running
    intervalRef.current = setInterval(() => {
      setQuickTransitionSupport((prev) => prev + 1); // Example update logic
    }, 100);
    // Cleanup on unmount or when dependency changes
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isLoading]);

  const toggleGraph = (type: string) => {
    setGraphTypes((prev) =>
      prev.map((graph) =>
        graph.type === type ? { ...graph, enabled: !graph.enabled } : graph
      )
    );
  };

  const handleFetchData = async () => {
    if (selectedTherapy) {
      await onSync(selectedTherapy, true, true, true); // Always fetch all 3
    }
  };

  const enabledGraphs = graphTypes.filter((graph) => graph.enabled);

  // Dynamic layout calculations for landscape
  const baseBrushHeight = 80;
  const totalBrushHeight = baseBrushHeight + bottomSpacing;
  const availableHeight = window.innerHeight - totalBrushHeight;

  const availableWidth = getAvailableWidth();
  const containerWidth = getContainerWidth();

  const getMinGraphHeight = () => {
    if (isMobile && enabledGraphs.length > 2) return 160;
    if (isMobile && enabledGraphs.length > 1) return 240;
    return 320;
  };

  const minGraphHeight = getMinGraphHeight();
  const graphHeight = Math.max(minGraphHeight, 180);

  const shouldShowTherapySelector = curvesList && curvesList.length > 0;

  // **PORTRAIT MODE - Responsive card layout**
  return (
    <>
      <div
        ref={setContainerRef}
        className="rounded-xl mb-4 overflow-hidden"
        style={{
          backgroundColor: "#1C1C1E",
          margin: "0 auto",
          maxWidth: "100%",
          overflowX: "hidden",
          marginBottom: "20px",
        }}
      >
        {/* Card Header with Therapy Selector */}
        <div
          className="flex justify-between items-start p-4 border-b"
          style={{
            borderColor: "rgba(255, 255, 255, 0.1)",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <h3
            className="text-white text-lg font-medium tracking-wide"
            style={{
              minWidth: "fit-content",
              flexShrink: 0,
            }}
          >
            CURVE ANALYSIS
          </h3>

          {/* Therapy Selection Button in Header */}
          {shouldShowTherapySelector && (
            <div
              className="flex justify-end w-full sm:w-auto"
              style={{ minWidth: "150px" }}
            >
              <button
                onClick={() => setShowTherapySelector(true)}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  padding: "4px 8px 4px 12px",
                  gap: "2px",
                  maxWidth: "100%",
                  minWidth: "150px",
                  height: "32px",
                  background: "#262626",
                  borderRadius: "30px",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <span
                  style={{
                    flex: 1,
                    height: "20px",
                    fontFamily: "Figtree, sans-serif",
                    fontStyle: "normal",
                    fontWeight: "400",
                    fontSize: "12px",
                    lineHeight: "20px",
                    display: "flex",
                    alignItems: "center",
                    textAlign: "center",
                    color: "#B0B4B7",
                    justifyContent: "center",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  Therapy date:{" "}
                  {selectedTherapy
                    ? `${selectedTherapy.date} ${selectedTherapy.time}`
                    : "Select Therapy"}
                </span>
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    transform: "rotate(90deg)",
                    flex: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IonIcon
                    icon={chevronForwardOutline}
                    style={{
                      color: "#B0B4B7",
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </div>
              </button>
            </div>
          )}
        </div>

        {/* Device Disconnection Notice */}
        {!isConnected && storedCurveDataDeviceId && data && (
          <div
            className="px-4 py-2 border-b"
            style={{
              backgroundColor: "#2A2A2C",
              borderColor: "rgba(255, 255, 255, 0.1)",
            }}
          >
            <p
              className="text-sm font-normal"
              style={{ color: "rgba(255, 255, 255, 0.6)" }}
            >
              Showing data from device: {storedCurveDataDeviceId}
            </p>
          </div>
        )}

        <div className="p-4" style={{ overflow: "hidden" }}>
          {/* Fetch Button - Show only when no data exists */}
          {selectedTherapy && !data && !isLoading && (
            <div className="flex flex-col items-center justify-center py-16">
              <button
                onClick={handleFetchData}
                style={{
                  background:
                    "linear-gradient(90deg, #01B7C4 0%, #00ACD7 100%)",
                  borderRadius: "30px",
                  fontFamily: "Figtree, sans-serif",
                  fontWeight: "600",
                  fontSize: "16px",
                  padding: "12px 25px",
                  height: "46px",
                  border: "none",
                  cursor: "pointer",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  transition: "all 0.2s ease",
                }}
              >
                <IonIcon
                  icon={sendSharp}
                  style={{
                    fontSize: "18px",
                    color: "white",
                  }}
                />
                Fetch from Device
              </button>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div>
              <CurvesLoading />
            </div>
          )}

          {/* Graph Controls and Content */}
          {!isLoading && data && (
            <>
              {/* Custom Checkboxes and Refresh Icon */}
              <div
                className="mb-6"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                {/* Checkboxes */}
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    flexWrap: "wrap",
                  }}
                >
                  {graphTypes.map((graph) => (
                    <CustomCheckbox
                      key={graph.type}
                      label={
                        graph.type.charAt(0).toUpperCase() + graph.type.slice(1)
                      }
                      checked={graph.enabled}
                      onChange={() => toggleGraph(graph.type)}
                    />
                  ))}
                </div>
              </div>
              {/* Individual Graph Containers - Responsive Width */}
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  alignItems: "center",
                  justifyContent: "flex-start",
                }}
              >
                {enabledGraphs.length > 0 ? (
                  enabledGraphs.map((graph) => {
                    const graphData =
                      graph.type === "flow"
                        ? data.flowData
                        : graph.type === "pressure"
                        ? data.pressureData
                        : data.volumeData;

                    return (
                      <HistoricalGraphComponent
                        key={graph.type}
                        data={graphData}
                        graphType={graph}
                        width={containerWidth} // Use full container width
                        height={graphHeight}
                        brushRange={brushRange}
                        isMobile={isMobile}
                        graphCount={enabledGraphs.length}
                        onExpand={forceEnterLandscape}
                        showExpandIcon={true}
                      />
                    );
                  })
                ) : (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "200px",
                      color: "#B0B4B7",
                      fontFamily: "Figtree, sans-serif",
                      background: "#1C1C1E",
                      borderRadius: "8px",
                      width: "100%",
                      maxWidth: containerWidth,
                    }}
                  >
                    Select at least one graph type to view data
                  </div>
                )}
              </div>

              {/* Range Slider - Full width responsive container */}
              <div
                style={{
                  width: "100%",
                  maxWidth: containerWidth,
                  margin: "0 auto",
                }}
              >
                {(() => {
                  const brushData = data.pressureData;
                  const brushDataType = "pressure";
                  const brushColor = "#22D3EE";

                  return (
                    <HistoricalBrushComponent
                      data={brushData}
                      brushRange={brushRange}
                      onBrushChange={setBrushRange}
                      allTimeRange={allTimeRange}
                      dataType={brushDataType}
                      dataColor={brushColor}
                      containerWidth={containerWidth}
                    />
                  );
                })()}
              </div>
            </>
          )}

          {/* Show message when no therapy selected */}
          {!selectedTherapy && shouldShowTherapySelector && !isLoading && (
            <div className="flex flex-col items-center justify-center py-16">
              <p className="text-gray-400 text-center mb-4">
                Select a therapy session to view curve analysis
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Therapy Selector Modal */}
      {shouldShowTherapySelector && (
        <TherapySelector
          isOpen={showTherapySelector}
          onClose={stableCallbacks.current.onClose}
          onSelect={stableCallbacks.current.onSelect}
          curvesList={modalData.curvesList}
          modalKey={modalData.modalKey}
        />
      )}

      {/* Enhanced Full Screen Landscape Modal with smooth transition */}
      {forcedLandscape && !!selectedLandscapeGraph && !isTransitioning && (
        <IonModal
          isOpen={
            forcedLandscape && !!selectedLandscapeGraph && !isTransitioning
          }
          showBackdrop={false}
          className="landscape-modal"
          enterAnimation={undefined}
          leaveAnimation={undefined}
        >
          <div
            className="min-h-screen bg-black text-white"
            style={{
              opacity: isTransitioning ? 0 : 1,
              transition: "opacity 0.2s ease-in-out",
            }}
          >
            <div
              style={{
                padding: "16px",
                background: "#000000",
                height: `${availableHeight}px`,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  justifyContent: "flex-start",
                  maxWidth: "100%",
                  height: "100%",
                }}
              >
                {/* Back Button Row */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    marginBottom: "8px",
                    minHeight: "30px",
                  }}
                >
                  <button
                    onClick={exitLandscape}
                    disabled={isTransitioning}
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: isTransitioning ? "not-allowed" : "pointer",
                      padding: "0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "opacity 0.2s ease",
                      opacity: isTransitioning ? 0.5 : 1,
                    }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15 18L9 12L15 6"
                        stroke="#B0B4B7"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>

                {(() => {
                  const selectedGraph = graphTypes.find(
                    (g) => g.type === selectedLandscapeGraph
                  );
                  if (!selectedGraph) return null;

                  const graphData =
                    selectedLandscapeGraph === "flow"
                      ? data?.flowData || []
                      : selectedLandscapeGraph === "pressure"
                      ? data?.pressureData || []
                      : data?.volumeData || [];

                  const landscapeGraphHeight = Math.max(
                    120,
                    availableHeight - 38 - (isMobile ? 25 : 20)
                  );

                  return (
                    <div style={{ flex: 1 }}>
                      <HistoricalGraphComponent
                        key={selectedGraph.type}
                        data={graphData}
                        graphType={selectedGraph}
                        width={availableWidth}
                        height={landscapeGraphHeight}
                        brushRange={brushRange}
                        isMobile={isMobile}
                        graphCount={1}
                        onExpand={() => {
                          console.log("Already in landscape");
                        }}
                        showExpandIcon={false}
                      />
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Range Slider */}
            <div
              style={{
                height: `${totalBrushHeight}px`,
                padding: "16px",
                paddingBottom: `${bottomSpacing}px`,
                background: "#000000",
                borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                position: "relative",
              }}
            >
              {(() => {
                let brushData = data?.pressureData || [];
                let brushDataType = "pressure";
                let brushColor = "#22D3EE";

                if (selectedLandscapeGraph === "flow") {
                  brushData = data?.flowData || [];
                  brushDataType = "flow";
                  brushColor = "#fbfb24";
                } else if (selectedLandscapeGraph === "volume") {
                  brushData = data?.volumeData || [];
                  brushDataType = "volume";
                  brushColor = "#f73434";
                }

                return (
                  <HistoricalBrushComponent
                    data={brushData}
                    brushRange={brushRange}
                    onBrushChange={setBrushRange}
                    allTimeRange={allTimeRange}
                    dataType={brushDataType}
                    dataColor={brushColor}
                    containerWidth={availableWidth}
                  />
                );
              })()}
            </div>
          </div>
        </IonModal>
      )}
    </>
  );
};

export default EnhancedCurveAnalysisCard;
